import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../../../services/AxiosInstance";
import { Link } from "react-router-dom";
import { DAY_ORDER, dayName, slotLabels } from "./flowRules";

// ── small controls ───────────────────────────────────────────────────────────
export const TagInput = ({ values = [], onChange, placeholder }) => {
  const [text, setText] = useState("");
  const add = () => {
    const parts = text.split(",").map((t) => t.trim()).filter(Boolean);
    if (parts.length) onChange([...new Set([...values, ...parts])]);
    setText("");
  };
  return (
    <div className="rf-tags">
      {values.map((v) => (
        <span className="rf-tag" key={v}>{v}<button type="button" onClick={() => onChange(values.filter((x) => x !== v))} aria-label={`Remove ${v}`}>×</button></span>
      ))}
      <input value={text} placeholder={values.length ? "" : placeholder}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } if (e.key === "Backspace" && !text && values.length) onChange(values.slice(0, -1)); }}
        onBlur={add} />
    </div>
  );
};

const Field = ({ label, help, children }) => (
  <div className="form-group">
    {label && <label className="rf-label d-block mb-1">{label}</label>}
    {children}
    {help && <div className="rf-help">{help}</div>}
  </div>
);

const HOURS = Array.from({ length: 25 }, (_, h) => h);
const hh = (h) => `${String(h % 24).padStart(2, "0")}:00`;

// ── one form per step type ───────────────────────────────────────────────────
const Schedule = ({ c, set }) => {
  const toggleDay = (d) => set({ days: c.days.includes(d) ? c.days.filter((x) => x !== d) : [...c.days, d] });
  const labels = slotLabels(c);
  return (
    <>
      <div className="row">
        <div className="col-6"><Field label="Posts per day"><input type="number" min="1" max="50" className="form-control" value={c.postsPerDay} onChange={(e) => set({ postsPerDay: Number(e.target.value) })} /></Field></div>
        <div className="col-6"><Field label="Max per week" help="0 = no limit"><input type="number" min="0" className="form-control" value={c.maxPerWeek} onChange={(e) => set({ maxPerWeek: Number(e.target.value) })} /></Field></div>
      </div>
      <Field label="Active hours">
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          <select className="form-control" value={c.windowStart} onChange={(e) => set({ windowStart: Number(e.target.value) })}>{HOURS.slice(0, 24).map((h) => <option key={h} value={h}>{hh(h)}</option>)}</select>
          <span>→</span>
          <select className="form-control" value={c.windowEnd} onChange={(e) => set({ windowEnd: Number(e.target.value) })}>{HOURS.slice(1).map((h) => <option key={h} value={h}>{hh(h)}</option>)}</select>
        </div>
        {labels.length > 0 && <div className="rf-help">Posts at {labels.slice(0, 12).join("  ")}{labels.length > 12 ? " …" : ""}</div>}
      </Field>
      <Field label="Days">
        <div className="rf-days">{DAY_ORDER.map((d) => <button key={d} type="button" className={`rf-day ${c.days.includes(d) ? "on" : ""}`} onClick={() => toggleDay(d)}>{dayName(d)}</button>)}</div>
      </Field>
      <Field label="Skip dates" help="No posts on these days, such as holidays.">
        <input type="date" className="form-control" value="" onChange={(e) => { if (e.target.value && !c.skipDates.includes(e.target.value)) set({ skipDates: [...c.skipDates, e.target.value].sort() }); }} />
        {c.skipDates.length > 0 && (
          <div className="d-flex flex-wrap mt-2" style={{ gap: 6 }}>
            {c.skipDates.map((d) => <span className="rf-tag" key={d}>{d}<button type="button" onClick={() => set({ skipDates: c.skipDates.filter((x) => x !== d) })} aria-label={`Remove ${d}`}>×</button></span>)}
          </div>
        )}
      </Field>
    </>
  );
};

const NewPost = ({ c, set }) => (
  <Field label="Check for new items every" help="Posts go out as soon as a new item is found and written.">
    <select className="form-control" value={c.checkEveryMin} onChange={(e) => set({ checkEveryMin: Number(e.target.value) })}>
      {[15, 30, 60, 120, 360].map((m) => <option key={m} value={m}>{m < 60 ? `${m} minutes` : `${m / 60} hour${m > 60 ? "s" : ""}`}</option>)}
    </select>
  </Field>
);

const AITopics = ({ c, set, ctx }) => (
  <>
    {ctx.hasAIKey === false && (
      <div className="rf-alert amber">No AI key is connected. Add one in <Link to="/flows-ai">AI &amp; Style</Link> before this flow can write posts.</div>
    )}
    <Field label="Topics" help="Press Enter after each. One topic per post, taken in turn.">
      <TagInput values={c.topics} onChange={(topics) => set({ topics })} placeholder="e.g. Today's best kurta deal" />
    </Field>
    <Field label="Extra instructions for the AI" help="On top of your AI settings (tone, language, audience).">
      <textarea rows={4} className="form-control" value={c.extraInstructions} onChange={(e) => set({ extraInstructions: e.target.value })} placeholder="e.g. Always end with our store link." />
    </Field>
  </>
);

const RSS = ({ c, set }) => (
  <>
    <Field label="Feed addresses" help="Press Enter after each. Most news sites and blogs have one at /feed or /rss. YouTube, Reddit and public Telegram links work too.">
      <TagInput values={c.feeds} onChange={(feeds) => set({ feeds })} placeholder="https://example.com/feed" />
    </Field>
    <div className="rf-help">Each article goes out as its title, summary and link. Add an AI rewrite step to have it written in your own words.</div>
  </>
);

const Filter = ({ c, set }) => (
  <>
    <Field label="Keep only posts with these words" help="Leave empty to keep everything."><TagInput values={c.include} onChange={(include) => set({ include })} placeholder="e.g. puma, sale" /></Field>
    <Field label="Leave out posts with these words"><TagInput values={c.exclude} onChange={(exclude) => set({ exclude })} placeholder="e.g. casino" /></Field>
  </>
);

const AIRewrite = ({ ctx }) => (
  <>
    {ctx.hasAIKey === false && <div className="rf-alert amber">No AI key is connected. Add one in <Link to="/flows-ai">AI &amp; Style</Link>.</div>}
    <p className="rf-help" style={{ fontSize: 13 }}>The AI rewrites each item in your channel's voice. Tone, language, audience and banned words come from your AI &amp; Style settings. Drafts below the quality bar are skipped.</p>
  </>
);

const EditText = ({ c, set }) => (
  <>
    <Field label="Add a first line"><input className="form-control" value={c.prepend} onChange={(e) => set({ prepend: e.target.value })} placeholder="e.g. 🔥 Deal alert" /></Field>
    <Field label="Add a last line"><input className="form-control" value={c.append} onChange={(e) => set({ append: e.target.value })} placeholder="e.g. Join our group for more" /></Field>
    <Field label="Links">
      {[["keep", "Keep links (adds the source link)"], ["remove", "Remove all links"], ["set", "Use my own link"]].map(([v, l]) => (
        <label key={v} className={`rf-opt ${c.linkMode === v ? "on" : ""}`}><input type="radio" checked={c.linkMode === v} onChange={() => set({ linkMode: v })} /> {l}</label>
      ))}
      {c.linkMode === "set" && <input className="form-control mt-1" value={c.setLink} onChange={(e) => set({ setLink: e.target.value })} placeholder="https://your-store.com" />}
    </Field>
    <Field label="Tags" help="Added at the end as hashtags."><TagInput values={c.tags} onChange={(tags) => set({ tags })} placeholder="e.g. deals" /></Field>
  </>
);

const PostTypes = ({ c, set }) => (
  <>
    <Field label="Post types">
      <label className="d-block"><input type="checkbox" checked={!!c.text} onChange={(e) => set({ text: e.target.checked })} /> Text</label>
      <label className="d-block"><input type="checkbox" checked={!!c.image} onChange={(e) => set({ image: e.target.checked })} /> Image</label>
      <label className="d-block text-muted"><input type="checkbox" disabled /> Video (coming later)</label>
    </Field>
    <div className="rf-help">Images are taken from the source article when it has one. AI image creation is coming later.</div>
  </>
);

const Approval = ({ c, set }) => (
  <>
    <Field label="Before a post goes out">
      <label className={`rf-opt ${c.mode === "approve" ? "on" : ""}`}><input type="radio" checked={c.mode === "approve"} onChange={() => set({ mode: "approve" })} /> <strong>I approve each</strong>
        <div className="rf-help">Written up to 2 hours early, then waits for you. A post you don't approve is skipped.</div></label>
      <label className={`rf-opt ${c.mode === "auto" ? "on" : ""}`}><input type="radio" checked={c.mode === "auto"} onChange={() => set({ mode: "auto" })} /> <strong>Publish on its own</strong>
        <div className="rf-help">Goes out at its time without asking.</div></label>
    </Field>
    {c.mode === "auto" && (
      <Field label={`Minimum quality score: ${c.minScore}`} help="Posts the AI rates lower than this wait for your approval instead.">
        <input type="range" min="40" max="100" step="5" value={c.minScore} onChange={(e) => set({ minScore: Number(e.target.value) })} style={{ width: "100%" }} />
      </Field>
    )}
    <label className="text-muted d-block"><input type="checkbox" disabled /> Message me on WhatsApp when a post waits (coming soon)</label>
  </>
);

const PostToChannel = ({ c, set, ctx }) => (
  <>
    <Field label="WhatsApp number" help="Posts go out from your connected number.">
      <input className="form-control" readOnly value={ctx.phone ? `+${String(ctx.phone).replace(/^\+/, "")}` : ctx.connected === false ? "No device connected" : "Your connected number"} />
    </Field>
    {ctx.connected === false && <div className="rf-alert amber">Connect a device first on the <Link to="/device">Devices page</Link>.</div>}
    <Field label="Channel">
      <select className="form-control" value={c.channelId} onChange={(e) => set({ channelId: e.target.value })}>
        <option value="">Choose a channel</option>
        {ctx.channels.filter((ch) => ch.channelJid).map((ch) => <option key={ch._id} value={ch._id}>{ch.name}</option>)}
      </select>
    </Field>
    {ctx.channels.length === 0 && <div className="rf-help">No channels yet. Add or sync one in <Link to="/channel-manager">Channel Scheduler</Link>.</div>}
  </>
);

const ChannelPosts = ({ c, set, ctx }) => {
  const [list, setList] = useState(null);
  const [err, setErr] = useState("");
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const cRef = useRef(c); cRef.current = c;
  const load = () => axiosInstance.get("flows/wa-sources", { timeout: 30000 }).then((r) => { setList(r.data.data || []); setErr(""); }).catch((e) => { setList([]); setErr(e?.response?.data?.message || e.message); });
  useEffect(() => { load(); }, []); // eslint-disable-line
  const toggle = (jid) => set({ channelJids: c.channelJids.includes(jid) ? c.channelJids.filter((x) => x !== jid) : [...c.channelJids, jid] });
  const follow = async () => {
    if (!link.trim()) return;
    setBusy(true);
    try {
      const { data } = await axiosInstance.post("flows/wa-sources/follow", { link: link.trim() }, { timeout: 60000 });
      setLink(""); await load();
      if (data.data && !c.channelJids.includes(data.data.jid)) set({ channelJids: [...new Set([...(cRef.current.channelJids || []), data.data.jid])] });
    } catch (e) { setErr(e?.response?.data?.message || e.message); }
    setBusy(false);
  };
  const destJid = (ctx.channels || []).find((ch) => ch._id === ctx.destChannelId)?.channelJid;
  return (
    <>
      <Field label="Read new posts from" help="Channels your connected number follows or owns. New posts reach us while the flow is active.">
        {list === null ? <div className="rf-help">Loading your channels…</div> : list.length === 0 ? <div className="rf-help">No channels found. Follow one below.</div> : (
          <div style={{ maxHeight: 220, overflowY: "auto" }}>
            {list.map((ch) => (
              <label key={ch.jid} className={`rf-opt ${c.channelJids.includes(ch.jid) ? "on" : ""}`} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="checkbox" checked={c.channelJids.includes(ch.jid)} disabled={ch.jid === destJid && !c.channelJids.includes(ch.jid)} onChange={() => toggle(ch.jid)} />
                <span style={{ flex: 1 }}>{ch.name}</span>
                <span className="rf-chip">{ch.role === "OWNER" ? "Owner" : ch.role === "ADMIN" ? "Admin" : "Following"}</span>
              </label>
            ))}
          </div>
        )}
      </Field>
      {list && c.channelJids.filter((j) => !list.some((x) => x.jid === j)).length > 0 && (
        <Field label="Also selected" help="These are not in your list right now (offline or unfollowed). Remove them if you do not need them.">
          <div className="d-flex flex-wrap" style={{ gap: 6 }}>
            {c.channelJids.filter((j) => !list.some((x) => x.jid === j)).map((j) => <span className="rf-tag" key={j}>{j.split("@")[0].slice(0, 14)}<button type="button" onClick={() => toggle(j)} aria-label="Remove">×</button></span>)}
          </div>
        </Field>
      )}
      {err && <div className="rf-alert red">{err}</div>}
      <Field label="Follow another channel" help="Paste its invite link. Your connected number will follow it.">
        <div className="d-flex" style={{ gap: 8 }}>
          <input className="form-control" placeholder="https://whatsapp.com/channel/…" value={link} onChange={(e) => setLink(e.target.value)} />
          <button type="button" className="btn-rf-o" disabled={busy || !link.trim()} onClick={follow}>{busy ? "…" : "Follow"}</button>
        </div>
      </Field>
      <label className="d-flex" style={{ gap: 8 }}>
        <input type="checkbox" checked={!!c.rightsConfirmed} onChange={(e) => set({ rightsConfirmed: e.target.checked })} />
        <span>I have the right to reuse these posts.<div className="rf-help">Copying other channels can break their copyright and WhatsApp's rules. Add <b>AI rewrite</b> so posts go out in your own words.</div></span>
      </label>
    </>
  );
};

const PANELS = { schedule: Schedule, newPost: NewPost, aiTopics: AITopics, channelPosts: ChannelPosts, rss: RSS, filter: Filter, aiRewrite: AIRewrite, editText: EditText, postTypes: PostTypes, approval: Approval, postToChannel: PostToChannel };

export const NodePanel = ({ node, onChange, ctx }) => {
  const P = PANELS[node.type];
  if (!P) return null;
  return <P c={node.config} set={(patch) => onChange({ ...node.config, ...patch })} ctx={ctx} />;
};
